import { Fragment, useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Pagination,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { handleRetrieveMemberPayments } from "../../../../redux/member-slices/payment.slice";

function Payments() {
  const dispatch = useDispatch();
  const [data, setData] = useState({ payments: [], pagination: {} });
  const [page, setPage] = useState(1);
  const { loadings } = useSelector((state) => state.otherSlice);
  const fetchPayments = async () => {
    const response = await dispatch(
      handleRetrieveMemberPayments({ page, limit: 10 })
    ).unwrap();
    setData(response || { payments: [], pagination: {} });
  };

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line
  }, [page]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency?.toUpperCase() || "INR",
      minimumFractionDigits: 2,
    }).format(amount / 100);
  };

  return (
    <Fragment>
      <Box mb={3}>
        <Typography variant="h5" fontWeight="600" color="other.color1">
          Payments
        </Typography>
        <Typography variant="body2" color="textSecondary">
          View your transaction history.
        </Typography>
      </Box>

      {loadings?.retrieveAll ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      ) : data?.payments?.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No payments found.
        </Alert>
      ) : (
        <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="payments table">
              <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "600" }}>Booking ID</TableCell>
                  <TableCell sx={{ fontWeight: "600" }}>Specialist</TableCell>
                  <TableCell sx={{ fontWeight: "600" }}>Booking Date</TableCell>
                  <TableCell sx={{ fontWeight: "600" }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: "600" }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: "600" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "600" }}>Payment Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.payments.map((row) => (
                  <TableRow
                    key={row._id}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      ...(row.isIncomplete && { bgcolor: "#fafafa" })
                    }}
                  >
                    <TableCell component="th" scope="row">
                      {row.bookingId ? (
                        row.bookingId.toString().slice(-6).toUpperCase()
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            Not Created
                          </Typography>
                          <Tooltip
                            title="This payment was initiated but the booking was not completed. This usually happens when payment is abandoned or fails during checkout."
                            arrow
                          >
                            <IconButton size="small" sx={{ p: 0 }}>
                              <InfoOutlinedIcon fontSize="small" color="action" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>
                      {row.specialistName}
                    </TableCell>
                    <TableCell>
                      {row.startTime ? (
                        moment(row.startTime).format("MM-DD-YYYY hh:mm A")
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {row.isIncomplete ? "Payment Incomplete" : "Not Available"}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="500">
                        {formatAmount(row.amount, row.currency)}
                      </Typography>
                      {row.totalRefunded > 0 && (
                        <Typography variant="caption" color="error">
                          Refunded: {formatAmount(row.totalRefunded, row.currency)}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={
                          row.type === "paid"
                            ? "Paid"
                            : row.type === "refund"
                              ? "Refund"
                              : "Incomplete"
                        }
                        color={
                          row.type === "paid"
                            ? "success"
                            : row.type === "refund"
                              ? "warning"
                              : "default"
                        }
                        size="small"
                        variant="outlined"
                        sx={{
                          fontWeight:
                            row.type === "paid" && row.status === "succeeded"
                              ? 700
                              : 400,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>
                      <Typography
                        variant="body2"
                        fontWeight={
                          row.type === "paid" && row.status === "succeeded"
                            ? 700
                            : 400
                        }
                        color={
                          row.status === "succeeded"
                            ? "success.main"
                            : row.status === "failed"
                              ? "error.main"
                              : row.status === "abandoned" || row.status === "pending"
                                ? "text.secondary"
                                : "text.primary"
                        }
                      >
                        {row.status?.replace("_", " ")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {moment(row.createdAt).format("MM-DD-YYYY hh:mm A")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {data.pagination?.totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2, borderTop: "1px solid", borderColor: "divider" }}>
              <Pagination
                count={data.pagination.totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </Paper>
      )}
    </Fragment>
  );
}

export default Payments;
